import { useCallback, useEffect, useRef, useState, type TouchEvent } from 'react';
import { Link } from 'react-router-dom';
import type { ContentItem } from '@/mocks/content';
import { formatShortDate } from '@/utils/content';

export default function HeroCarousel({ items }: { items: ContentItem[] }) {
  const [active, setActive] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timer = useRef<number | null>(null);

  const goTo = useCallback(
    (index: number) => setActive((index + items.length) % items.length),
    [items.length]
  );

  useEffect(() => {
    if (items.length <= 1) return;
    timer.current = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [items.length]);

  if (items.length === 0) return null;

  const handleTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) goTo(active + (delta < 0 ? 1 : -1));
    touchStartX.current = null;
  };

  return (
    <section
      className="relative h-[420px] w-full overflow-hidden bg-foreground-950 md:h-[600px]"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {items.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <img
            src={item.image}
            alt={item.title}
            title={`${item.title} - ${item.tag}`}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/25"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
              <div className="max-w-2xl">
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <span className="rounded bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50 dark:text-foreground-950">
                    {item.categoryName} · {item.tag}
                  </span>
                  <span className="text-xs text-background-200">{formatShortDate(item.date)}</span>
                </div>
                <h2 className="mb-4 font-heading text-2xl font-bold leading-tight text-background-50 md:text-4xl">
                  {item.title}
                </h2>
                <p className="mb-7 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
                  {item.summary}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to={`/detail/${item.id}`}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
                  >
                    阅读全文
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <Link
                    to={item.category === 'news' ? '/news' : item.category === 'entertainment' ? '/entertainment' : '/lifestyle'}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
                  >
                    更多{item.categoryName}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        aria-label="上一张"
        onClick={() => goTo(active - 1)}
        className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/35 md:left-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => goTo(active + 1)}
        className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/35 md:right-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${index + 1} 张`}
            onClick={() => goTo(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === active ? 'w-7 bg-accent-500' : 'w-2 bg-background-50/50 hover:bg-background-50/80'
            }`}
          />
        ))}
      </div>
    </section>
  );
}